// src/pages/Register.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiUserPlus } from 'react-icons/fi';
import { register } from '../services/api/auth';
import { Input, Button, FormField, GoogleButton, OAuthDivider } from '../components/ui';
import { useToast } from '../components/ui/Toast';
import logo from '../images/logo_transparent.png';
import logoWhite from '../images/logo_transparent_white.png';

const Register = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleRegister = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const userData = await register(formData);
            toast.success('Account created! Let\'s get you set up.');

            // Store token and redirect to onboarding
            localStorage.setItem('token', userData.access_token);
            localStorage.setItem('user', JSON.stringify({
                id: userData.user_id,
                email: userData.email,
                username: userData.username,
                organizations: userData.organizations
            }));

            navigate('/onboarding');
        } catch (error) {
            console.error('Registration error:', error);
            toast.error(error?.response?.data?.detail?.[0]?.msg || error?.response?.data?.detail || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen">
            {/* Left: Form Section */}
            <div className="flex w-full flex-col justify-center px-4 py-10 sm:px-6 lg:w-1/2 lg:px-20 xl:px-24">
                <div className="mx-auto w-full max-w-md">
                    {/* Logo */}
                    <div className="mb-10">
                        <Link to="/" className="inline-block">
                            <img src={logo} alt="Refinal" className="h-10" />
                        </Link>
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-black dark:text-white sm:text-title-md">
                            Create your account
                        </h2>
                        <p className="mt-2 text-body">
                            Get started in seconds
                        </p>
                    </div>

                    {/* Google Sign Up */}
                    <GoogleButton text="Sign up with Google" />

                    {/* Divider */}
                    <OAuthDivider />

                    {/* Form */}
                    <form onSubmit={handleRegister} className="space-y-6">
                        <FormField
                            label="Email"
                            htmlFor="email"
                            required
                            error={errors.email}
                        >
                            <div className="relative">
                                <Input
                                    type="email"
                                    name="email"
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={errors.email}
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-body">
                                    <FiMail size={20} />
                                </span>
                            </div>
                        </FormField>

                        <FormField
                            label="Password"
                            htmlFor="password"
                            required
                            error={errors.password}
                            hint="At least 8 characters"
                        >
                            <div className="relative">
                                <Input
                                    type="password"
                                    name="password"
                                    placeholder="Create a password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={errors.password}
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-body">
                                    <FiLock size={20} />
                                </span>
                            </div>
                        </FormField>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            icon={FiUserPlus}
                            iconPosition="right"
                            loading={isLoading}
                            className="w-full"
                        >
                            Create Account
                        </Button>
                    </form>

                    {/* Sign In Link */}
                    <div className="mt-6 text-center">
                        <p className="text-body">
                            Already have an account?{' '}
                            <Link
                                to="/login"
                                className="font-medium text-brand-500 hover:text-brand-600 transition-colors"
                            >
                                Sign In
                            </Link>
                        </p>
                    </div>
                </div>
            </div>

            {/* Right: Branding Section */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 flex-col justify-center items-center p-12 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                </div>

                {/* Content */}
                <div className="relative z-10 text-center">
                    <img src={logoWhite} alt="Refinal" className="h-12 mx-auto mb-8" />
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Start Your Financial Journey
                    </h2>
                    <p className="text-xl text-white/80 max-w-md">
                        Join thousands of companies using REFINAL to track and analyze their financial data.
                    </p>

                    {/* Stats */}
                    <div className="mt-12 grid grid-cols-2 gap-8 max-w-sm mx-auto">
                        <div className="text-center">
                            <div className="text-4xl font-bold text-white">10K+</div>
                            <div className="text-white/70 mt-1">Transactions/day</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-white">500+</div>
                            <div className="text-white/70 mt-1">Companies</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-white">99.9%</div>
                            <div className="text-white/70 mt-1">Uptime</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-white">24/7</div>
                            <div className="text-white/70 mt-1">Support</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
